import { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import jsQR from "jsqr";
import {
    getEventAttendanceDetails,
    getCheckInDetailsByTicketId,
    markedAttendanceByTicketId,
    getAttendeesHistory,
    getAttendeeTicketDetails,
    manualSearchTicket
} from "../../../../app/store/slices/event/team/team";
import { formatEventDate } from "../../../../utils/dateFormatter";
import { EVENT_TYPES, EVENT_STATUS } from "../../../../hooks/event-hooks/useEventStatus";
import { successToastTopRight } from "../../../../utils/toasts/toasts";
import { Body2 } from "../../../../themes/typography";

/**
 * 1. Disable scanning manual search when event status is drafter or cancelled
 * 2. Show inactive status when event is not started or event ended for scanner
 */

// {"event_id": 501, "ticket_type_id": 1020, "ticket_number":"TIX45852594"}

export default function useEventAttendance() {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);

    const [data, setData] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    /**
     * State to store the recent scans entries
     */
    const [recentScans, setRecentScans] = useState([]);
    const [showRecentScans, setShowRecentScans] = useState(false)
    const [filteredRecentScans, setFilteredRecentScans] = useState([]);


    /**
     * Switch to view details tab and store the detail in a state after api call
     */
    const [viewDetailsTab, setViewDetailsTab] = useState(false);
    const [viewDetailsData, setViewDetailsData] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [processLoaderType, setProcessLoaderType] = useState('checkIn') // checkIn || rejected || viewDetails || recentScans


    /**
     * State to handle menus that open on elipsis (3 dot icon)
     */
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);


    /**
     * Manual Ticket Search States
     */
    const [manualSearchTerm, setManualSearchTerm] = useState('')
    const [manualSearchResult, setManualSearchResult] = useState(null)
    const [manualSearchLoading, setManualSearchLoading] = useState(false)
    const [openManualSearchModalType, setOpenManualSearchModalType] = useState(null)


    /**
     * Set the countdown until event starts and make it active/de-active
     */
    const [countdown, setCountdown] = useState(null);
    const [isCountdownActive, setIsCountdownActive] = useState('on'); // off || on || eventEnded


    /**
     * Ref to scroll down to event schedule section in UI
     */
    const multipleDaysRef = useRef(null);


    /**
     * Animation frame reference for the QR scanning loop
     * 
     * This ref stores the requestAnimationFrame ID that controls the continuous.
     * It allows starting and stopping the QR code scanning loop efficiently.
     */
    const animationRef = useRef(null);


    /**
     * Where the live camera feed is shown (<video>)
     */
    const videoRef = useRef(null);


    /**
     * Holds the MediaStream so you can stop it later
     */
    const streamRef = useRef(null);


    /**
     * UI control flags
     */
    const [isCameraOn, setIsCameraOn] = useState(false);
    const [isCameraInitializing, setIsCameraInitializing] = useState(false);


    /**
     * Holds the latest QR code result to display
     */
    const [qrResult, setQrResult] = useState(null);


    /**
     * Holds scan result based modal and keep it open/close
     */
    const [scanResult, setScanResult] = useState('validTicket');
    const [openScanResultModal, setOpenScanResultModal] = useState(false);


    /**
     * Holds the open scan result modal value to sync immediately for further checks
     */
    const openModalRef = useRef(openScanResultModal);
    useEffect(() => { openModalRef.current = openScanResultModal; }, [openScanResultModal]);


    /**
     * True while qr code detected to avoid multiple api calls and scanning process
     */
    const processingRef = useRef(false);


    /**
     * Holds the last qr data with timestamp
     */
    const lastDetectedRef = useRef({ value: null, ts: 0 });


    /**
     * Cooldown period to ignore repeated scans of the same QR code
     */
    const SCAN_COOLDOWN_MS = 1000;

    /**
     * Canvas and context for QR code detection processing
     * 
     * Canvas: Hidden HTML5 canvas element used to capture video frames
     * - Draws current video frame as static image for analysis
     * - Provides pixel data that jsQR library can process
     * - Created in memory (not rendered in DOM) for performance
     * 
     * Context (ctx): 2D rendering context of the canvas
     * - Provides drawing methods like drawImage() to copy video frames
     * - Enables getImageData() to extract raw pixel data
     * - Required interface between video element and QR detection algorithm
     */
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");


    /**
     * @async
     * @function fetchEventDetails
     * @description To fetch the event details if user found
     * 1. Set loading state to true
     * 2. Get sessionId from localStorage
     * 3. Dispatch Redux action to fetch event attendance details
     * 4. On success, store the event data in state
     * 5. On error, store error details in state
     * 6. Always clear loading state after operation completes
     * 7. Only run if user and user.id exists
     */
    useEffect(() => {
        async function fetchEventDetails() {
            try {
                setLoading(true);

                const sessionId = localStorage.getItem('sessionId');

                const response = await dispatch(getEventAttendanceDetails(sessionId)).unwrap();

                setData(response.data);

            } catch (error) {
                // console.error("Error fetching event details:", error);
                setError({
                    data: error?.data || null,
                    code: error?.code || null,
                    message: error?.message || "An unexpected error occurred."
                });
            } finally {
                setLoading(false);
            }
        }

        if (user && user?.id) {
            fetchEventDetails();
        }
    }, [user?.id])


    /**
     * @function formatEventDates
     * @description to format event dates based on event type
     * - multiDay: shows "startDate - endDate"
     * - continuous: shows "startDate to endDate"
     * - single day: shows "eventDate"
     *
     * @returns {string} formatted event date(s)
     */
    function formatEventDates() {
        const eventType = data?.event?.event_type;
        const schedules = data?.event?.event_schedules || [];

        // If no schedules exist, return an empty string
        if (schedules.length === 0) return "";

        // Extract first and last schedule dates
        const firstDate = schedules[0]?.date;
        const lastDate = schedules[schedules.length - 1]?.date;

        switch (eventType) {
            case EVENT_TYPES.multiDay:
                return `${formatEventDate(firstDate)} - ${formatEventDate(lastDate)}`;

            case EVENT_TYPES.continuous:
                return `${formatEventDate(firstDate)} to ${formatEventDate(lastDate)}`;

            default:
                return formatEventDate(firstDate);
        }
    }


    /**
     * @function formatEventTiming
     * @description to format event timings based on event type
     * - single: shows "fromTime - toTime" (first schedule slot)
     * - multiDay: shows "Multiple Days"
     * - continuous: shows "startDate fromTime - endDate toTime"
     *
     * @returns {string} formatted event timing
     */
    function formatEventTiming() {
        const eventType = data?.event?.event_type;
        const schedules = data?.event?.event_schedules || [];

        // If no schedules exist, return an empty string
        if (schedules.length === 0) return "";

        // Extract first and last schedule
        const firstSchedule = schedules[0];
        const lastSchedule = schedules[schedules.length - 1];

        switch (eventType) {
            case EVENT_TYPES.multiDay:
                return (
                    <div
                        ref={multipleDaysRef}
                        onClick={scrollToMultipleDays}
                        style={{ cursor: 'pointer' }}
                    >
                        <Body2 color="var(--tertiary-color)">
                            Multiple Days
                        </Body2>
                    </div>
                );

            case EVENT_TYPES.continuous:
                return `${firstSchedule?.from_time} - ${lastSchedule?.to_time}`;

            default:
                // --- Check for multiple slots on single day ---
                const slotSchedules = schedules.filter(s => s.type === "slot");

                if (slotSchedules.length > 1) {
                    // Multiple slots: show similar to Multiple Days
                    return (
                        <div
                            ref={multipleDaysRef}
                            onClick={scrollToMultipleDays}
                            style={{ cursor: 'pointer' }}
                        >
                            <Body2 color="var(--tertiary-color)">
                                Multiple Slots
                            </Body2>
                        </div>
                    );
                } else {
                    // Single slot: show its from_time - to_time
                    return `${firstSchedule?.from_time} - ${firstSchedule?.to_time}`;
                }
        }
    }


    /**
     * Setup the countdown until permissions from_time reaches current time
     * 1. Do not process if permission not found
     * 2. Define variable to store countdown times
     * 3. If only one permission, set countdown start/end from it
     * 4. If multiple permissions, find earliest start and latest end times
     * 5. Setup interval to update countdown every second
     * 6. If current time is before start, show countdown to start (inactive)
     * 7. If current time is between start and end, show countdown to end (active)
     * 8. If current time is after end, show "Event Ended"
     * 9. Clear interval on component unmount
     * 10. Re-run effect if permissions data changes
     */
    useEffect(() => {
        if (!data?.permissions || data?.permissions?.length === 0) return;

        let countDownStart, countDownEnd;

        if (data?.permissions.length === 1) {
            const { to_time, date, check_in_time } = data?.permissions[0].schedule;

            countDownStart = dayjs(check_in_time);
            countDownEnd = dayjs(`${date} ${to_time}`);

        } else {

            const sorted = [...data?.permissions].sort(
                (a, b) => dayjs(a.schedule.check_in_time) - dayjs(b.schedule.check_in_time)
            );

            countDownStart = dayjs(sorted[0].schedule.check_in_time);

            const lastPermission = sorted[sorted.length - 1].schedule;
            countDownEnd = dayjs(`${lastPermission.date} ${lastPermission.to_time}`);

        }

        const timer = setInterval(() => {
            const now = dayjs();

            if (now.isBefore(countDownStart)) {
                setIsCountdownActive('on');
                setCountdown(formatCountdown(countDownStart.diff(now, "second")));
            } else if (now.isBefore(countDownEnd)) {
                setIsCountdownActive('off');
                setCountdown(formatCountdown(countDownEnd.diff(now, "second")));
            } else {
                setIsCountdownActive('windowEnded');
                setCountdown("0 seconds");
                clearInterval(timer);
            }
        }, 1000);

        return () => {
            clearInterval(timer);
        };
    }, [data?.permissions]);


    /**
     * @async
     * @function startQRScanner
     * @description Manage the camera opening and handle the stream
     * 1. Set loading state to show user that camera is initializing
     * 2. Check if we already have an active camera stream (when returning from other tab switch)
     * 3. Reuse existing stream instead of requesting camera permission again
     * 4. Only start QR detection if not already running (prevent duplicate loops)
     * 5. Update UI state to show camera is active
     * 6. Request new camera stream with rear-facing camera preference
     * 7. Store stream reference for later reuse and cleanup
     * 8. Connect camera stream to video element for display
     * 9. Start continuous QR code scanning from video frames
     * 10. Update UI state to show camera is successfully active
     * 11. Handle camera access errors (permission denied, no camera, etc.)
     * 12. Always reset loading state regardless of success or failure
     */
    async function startQRScanner() {
        try {
            setIsCameraInitializing(true);

            if (streamRef.current && streamRef.current.active) {

                if (videoRef.current) {
                    videoRef.current.srcObject = streamRef.current;
                    await videoRef.current.play();

                    if (!animationRef.current) {
                        startQRDetection();
                    }
                }

                setIsCameraOn(true);
                setIsCameraInitializing(false);
                return;
            }


            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: "environment" },
            })


            streamRef.current = stream;


            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                await videoRef.current.play();

                startQRDetection();
            }

            setIsCameraOn(true);
        } catch (err) {
            console.error("Error accessing camera:", err);
            setIsCameraOn(false);
        } finally {
            setIsCameraInitializing(false);
        }
    }


    /**
     * @function startQRDetection
     * @description handle the qr code detection
     * 1. Exit if video frame does not exist
     * 2. Limit scanning to specific FPS
     * 3. Stop scanning when modal is open
     * 4. Deduplicate QR detections using refs and cooldown
     */
    function startQRDetection() {

        // 1) Get the video element reference
        const video = videoRef.current;
        if (!video) return;


        // Track the last scan time for frame rate control,
        let lastScanTime = 0;
        const FPS = 5;
        const FRAME_INTERVAL = 1000 / FPS;


        /**
         * Core loop: scanForQR
         * - Runs on each animation frame
         * - Draws current video frame to canvas
         * - Extracts QR code if present
         * - Applies cooldown & deduplication
         */
        function scanForQR(timestamp) {

            // 2. If modal is open, stop scanning
            if (openModalRef.current) {
                if (animationRef.current) {
                    cancelAnimationFrame(animationRef.current);
                    animationRef.current = null;
                }
                return;
            }


            // 3. Ensure video has enough data for scanning
            if (video.readyState === video.HAVE_ENOUGH_DATA) {

                // Run scanning only if enough time has passed since last scan
                if (!lastScanTime || timestamp - lastScanTime >= FRAME_INTERVAL) {

                    // Prepare canvas with current video frame
                    canvas.width = video.videoWidth;
                    canvas.height = video.videoHeight;
                    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

                    // Extract pixel data for QR analysis
                    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                    const qrCode = jsQR(imageData.data, canvas.width, canvas.height);


                    // 4. If QR code found
                    if (qrCode) {
                        const qValue = qrCode.data;
                        const now = Date.now();

                        // If already processing a QR, skip further detection
                        if (processingRef.current) {
                            // Do nothing, still handling previous QR
                        }
                        else {
                            // If same QR detected recently within cooldown, skip
                            if (lastDetectedRef.current.value === qValue && (now - lastDetectedRef.current.ts) < SCAN_COOLDOWN_MS) {
                                // Skip duplicate within cooldown window
                            }
                            else {
                                // Mark QR as "being processed" and record details
                                processingRef.current = true;
                                lastDetectedRef.current = { value: qValue, ts: now };

                                handleQRCodeDetected(qrCode).catch((e) => {

                                    // Reset processing flag after cooldown if error occurs
                                    console.error("handleQRCodeDetected error:", e);
                                    setTimeout(() => { processingRef.current = false; }, SCAN_COOLDOWN_MS);
                                });
                            }
                        }
                    }

                    // Update last scan timestamp
                    lastScanTime = timestamp;
                }
            }

            // Schedule next frame for scanning
            animationRef.current = requestAnimationFrame(scanForQR);
        }

        // 5. Start the scanning loop only if not already running
        if (!animationRef.current) {
            animationRef.current = requestAnimationFrame(scanForQR);
        }
    }


    /**
     * @async
     * @function handleQRCodeDetected
     * @description Process detected QR code
     * 1. Stop scanning loop to prevent multiple detections
     * 2. Store raw QR data with timestamp
     * 3. Parse QR data and validate structure
     * 4. Validate event ID matches current event
     * 5. Check user has permission for ticket type
     * 6. Validate current time against permission schedule
     * 7. Fetch ticket details from backend using ticket ID
     * 8. Handle various validation failures with appropriate modals
     * 9. On success, show valid ticket modal with details and also show already checked-in if matches with the criteria
     */
    async function handleQRCodeDetected(qrCode) {

        // Step 1
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
            animationRef.current = null;
        }

        // Step 2
        setQrResult({
            data: qrCode.data,
            timestamp: new Date().toISOString()
        });

        try {
            const qrData = parseQRCodeData(qrCode.data);

            function openModalWithResult(resultType, logMessage) {
                // console.log(logMessage, qrData);
                setScanResult(resultType);
                setOpenScanResultModal(true);
            };


            // Step 3
            if (!qrData || !qrData.event_id || !qrData.ticket_type_id || !qrData.ticket_id) {
                openModalWithResult('invalidQrCodeTicket', "Invalid QR Data structure:");
                return;
            }


            // Step 4
            if (qrData.event_id !== data?.event?.event_id) {
                openModalWithResult('wrongEventTicket', "Wrong event QR data:");
                return;
            }


            // Step 5
            const hasPermission = data?.permissions?.some(ticket => ticket.id === qrData.ticket_type_id);

            if (!hasPermission) {
                openModalWithResult('enoughPermissionsTicket', "Insufficient permissions QR data:");
                return;
            }


            // Step 6
            const currentTime = dayjs();
            const ticketPermission = data?.permissions?.find(p => p.id === qrData.ticket_type_id);

            if (ticketPermission) {
                const { date, to_time, check_in_time } = ticketPermission.schedule;

                const checkInTime = dayjs(check_in_time);
                const checkInEndTime = dayjs(`${date} ${to_time}`);

                if (currentTime.isBefore(checkInTime)) {
                    openModalWithResult('tooEarlyTicket', "Too early for check-in:");
                    return;
                }

                if (currentTime.isAfter(checkInEndTime)) {
                    openModalWithResult('eventExpiredTicket', "Event has ended:");
                    return;
                }
            }


            // Step 7
            const response = await dispatch(getCheckInDetailsByTicketId({
                ticketId: qrData.ticket_id,
                // ticketId: 'TIX-11455422',
                sessionId: localStorage.getItem('sessionId') || null
            })).unwrap();


            setViewDetailsData((prev) => ({
                ...prev,
                ...response?.data?.ticket
            }))


            // Step 9
            if (response.code === 200 && response?.data?.attendances?.length > 0) {
                setScanResult('alreadyCheckedInTicket');
                setOpenScanResultModal(true);
            } else {
                setScanResult('validTicket');
                setOpenScanResultModal(true);
            }

            /**
             * On backend order
             * 1. Invalid Ticket Number
             * 2. Wrong Event Ticket Number
             * 3. In-sufficient Permission
             * 4. Too early
             * 5. slot ended / event ended
             * 6. already checked in
             * 7. valid
             */

        } catch (error) {
            console.error("Error parsing QR code:", error);

            // Step 8
            if (error.code === 404) {
                setScanResult('wrongEventTicket');
                setOpenScanResultModal(true);
                setQrResult(prev => ({ ...prev, error: "Ticket not found" }));
                return;
            }

            setScanResult('invalidQrCodeTicket');
            setOpenScanResultModal(true);
            setQrResult(prev => ({ ...prev, error: "Invalid QR code format" }));
        }
    }

    // console.log("View Details Data -> ", viewDetailsData)


    /**
     * @async
     * @function checkInGuestByTicketId
     * @description To check-in a guest's attendance by ticket ID
     * 1. Set loading state and process type
     * 2. Dispatch Redux action to mark attendance as checked-in in backend
     * 3. On success, close the modal to reflect the change
     * 4. TODO: Handle errors gracefully and log them
     * 5. Always clear loading state after operation completes
     */
    async function checkInGuestByTicketId() {
        try {

            setProcessLoaderType('checkIn')
            setDetailsLoading(true)

            const response = await dispatch(markedAttendanceByTicketId({
                ticketId: scanResultData.ticketId,
                // ticketId: 'TIX35364769',
                action: 'checked_in',
                checkInById: data?.scanner?.scanner_id,
                sessionId: localStorage.getItem('sessionId') || null
            })).unwrap();

            if (response.code === 200) {
                closeModal();
            }
        } catch (error) {
            console.error("Error in check-in:", error);
        } finally {
            setDetailsLoading(false)
        }
    }


    /**
     * @async
     * @function rejectGuestByTicketId
     * @description To reject a guest's attendance by ticket ID
     * 1. Set loading state and process type
     * 2. Dispatch Redux action to mark attendance as rejected in backend
     * 3. On success, close the modal to reflect the change
     * 4. TODO: Handle errors gracefully and log them
     * 5. Always clear loading state after operation completes
     */
    async function rejectGuestByTicketId() {
        try {

            setProcessLoaderType('reject')
            setDetailsLoading(true)

            const response = await dispatch(markedAttendanceByTicketId({
                ticketId: scanResultData.ticketId,
                // ticketId: 'TIX35364769',
                action: 'rejected',
                checkInById: data?.scanner?.scanner_id,
                sessionId: localStorage.getItem('sessionId') || null
            })).unwrap();

            if (response.code === 200) {
                closeModal();
            }
        } catch (error) {
            console.error("Error in check-in:", error);
        } finally {
            setDetailsLoading(false)
        }
    }


    /**
     * @async
     * @function onViewTicketDetails
     * @description To fetch and display detailed ticket information
     * 1. Set loading state and process type
     * 2. Dispatch Redux action to fetch ticket details from backend
     * 3. On success, switch to details tab and store ticket data in state
     * 4. TODO: Handle errors gracefully and log them
     * 5. Always clear loading state after operation completes
     */
    async function onViewTicketDetails() {
        try {
            setDetailsLoading(true);
            setProcessLoaderType('viewDetails')

            // Pass ticket_number here as ticketID
            const response = await dispatch(getAttendeeTicketDetails({
                // ticketId: 411
                ticketId: scanResultData.ticketId
            })).unwrap();

            if (response?.code === 200 && response?.success) {
                setViewDetailsTab(true);

                setViewDetailsData((prev) => ({
                    ...prev,
                    ...response?.data || null
                }))
            }
        } catch (error) {
            console.error("Error in viewing ticket details:", error);
        } finally {
            setDetailsLoading(false);
        }
    }


    /**
     * @async
     * @function getAttendeesRecentHistory
     * @description To fetch the recent scans history of attendees
     * 1. Show recent scans view and set loading state
     * 2. Dispatch Redux action to fetch attendance history from backend
     * 3. On success, store the attendances in state for display
     * 4. TODO: Handle errors gracefully and log them
     * 5. Always clear loading state after operation completes
     */
    async function getAttendeesRecentHistory() {
        try {
            setShowRecentScans(true)
            setDetailsLoading(true);
            setProcessLoaderType('recentScans')

            const response = await dispatch(getAttendeesHistory({
                eventId: data?.event?.event_id || null,
                userId: data?.scanner?.scanner_id || null
            })).unwrap();


            if (response?.code === 200 && response?.success) {
                const attendances = response?.data?.attendances || [];
                setRecentScans(attendances);
                setFilteredRecentScans(attendances);
            }
        } catch (error) {
            console.error("Error in viewing ticket details:", error);
        } finally {
            setDetailsLoading(false);
        }
    }


    /**
     * @function onRecentScanSearch
     * @description To filter out the records based on user search query
     * 1. Capture search input
     * 2. Set loading state to show processing
     * 3. Use setTimeout to allow loading state to render
     * 4. If search is empty, reset to full list
     * 5. Filter recentScans by ticket_name, ticket_number, and action fields
     * 6. Handle special case for "checked_in" action to match "checked in"
     * 7. Update filtered list state and clear loading state
     * 8. Add slight delay to loading state for better UX
     * 
     * @param {object} e - The event object from the search input
     * @returns {void}
     */
    function onRecentScanSearch(e) {
        const searchTerm = e.target.value;

        setDetailsLoading(true);

        setTimeout(() => {
            if (!searchTerm.trim()) {
                setFilteredRecentScans(recentScans);
                setDetailsLoading(false);
                return;
            }

            const filtered = recentScans.filter(item => {
                const searchLower = searchTerm.toLowerCase();

                let actionText = item.action?.toLowerCase() || '';

                if (actionText === 'checked_in') {
                    actionText = 'checked in';
                }

                return (
                    item.ticket_name?.toLowerCase().includes(searchLower) ||
                    item.ticket_number?.toLowerCase().includes(searchLower) ||
                    actionText.includes(searchLower) ||
                    item.action?.toLowerCase().includes(searchLower)
                );
            });

            setFilteredRecentScans(filtered);
            setDetailsLoading(false);
        }, 300);
    }


    /**
     * @function cleanup useEffect
     * @description Component cleanup when useEventAttendance hook unmounts
     * 1. Stop the QR scanning animation loop to prevent memory leaks
     * 2. Close camera and release all media resources (stream, video element)
     * 3. Reset QR result state to clear any displayed scan data
     */
    useEffect(() => {
        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
            closeCamera();
            setQrResult(null);
        };
    }, []);


    /**
     * @function tabSwitchVideoRestore useEffect
     * @description Restore video stream when switching back to scanner tab
     * 1. Check if user is on scanner tab with active camera but disconnected video
     * 2. Reconnect the existing stream to video element and resume playback
     * 3. Restart QR detection loop if it was stopped during tab switch
     */
    useEffect(() => {
        if (activeTab === 1 && isCameraOn && videoRef.current && streamRef.current) {

            if (!videoRef.current.srcObject) {
                videoRef.current.srcObject = streamRef.current;
                videoRef.current.play().catch(console.error);

                if (!animationRef.current) {
                    startQRDetection();
                }
            }
        }
    }, [activeTab, isCameraOn]);


    function onChangeManualSearch(e) {

        const value = e.target.value
        setManualSearchTerm(value)

        // Check if value is null or empty string, don't call API if empty
        if (!value || value.trim() === '') {
            setManualSearchLoading(false)
            setManualSearchResult(null)
            setOpenManualSearchModalType(null)
            return
        }
    }

    async function onManualTicketSearch() {
        try {
            setViewDetailsTab(false)
            setViewDetailsData(null)
            setManualSearchLoading(true)

            const payload = {
                event_id: data?.event?.event_id,
                ticket_number: manualSearchTerm,
                sessionId: localStorage.getItem('sessionId') || null
            }

            const response = await dispatch(manualSearchTicket({
                payload,
                sessionId: localStorage.getItem('sessionId') || null
            })).unwrap();

            if (response?.code === 200 && response?.success) {
                setManualSearchResult(response?.data)
                setOpenManualSearchModalType(response?.data?.type)
            }

        } catch (error) {
            console.error("Error searching tickets:", error)
        } finally {
            setManualSearchLoading(false)
        }
    }

    async function onManualSearchCheckIn() {
        try {

            setProcessLoaderType('checkIn')
            setDetailsLoading(true)

            const response = await dispatch(markedAttendanceByTicketId({
                ticketId: manualSearchResult?.ticket?.ticket_id,
                action: 'checked_in',
                checkInById: data?.scanner?.scanner_id,
                sessionId: localStorage.getItem('sessionId') || null
            })).unwrap();

            if (response.code === 200) {
                onManualResultDismiss();
            }

            setProcessLoaderType(null);
            successToastTopRight("Guest checked in successfully!");
        } catch (error) {
            console.error("Error in check-in:", error);
        } finally {
            setDetailsLoading(false)
        }
    }

    async function onManualSearchReject() {
        try {

            setProcessLoaderType('reject')
            setDetailsLoading(true)

            const response = await dispatch(markedAttendanceByTicketId({
                ticketId: manualSearchResult?.ticket?.ticket_id,
                action: 'rejected',
                checkInById: data?.scanner?.scanner_id,
                sessionId: localStorage.getItem('sessionId') || null
            })).unwrap();

            if (response.code === 200) {
                onManualResultDismiss();
            }

            setProcessLoaderType(null);
            successToastTopRight("Guest rejected successfully!");
        } catch (error) {
            console.error("Error in check-in:", error);
        } finally {
            setDetailsLoading(false)
        }
    }

    async function onViewManualSearchTicketDetails() {
        try {
            setDetailsLoading(true);
            setProcessLoaderType('viewDetails')

            const response = await dispatch(getAttendeeTicketDetails({
                ticketId: manualSearchResult?.ticket?.ticket_id
            })).unwrap();

            if (response?.code === 200 && response?.success) {
                setViewDetailsTab(true);

                setViewDetailsData((prev) => ({
                    ...prev,
                    ...response?.data || null
                }))
            }
        } catch (error) {
            console.error("Error in viewing ticket details:", error);
        } finally {
            setDetailsLoading(false);
        }
    }


    function onManualResultDismiss() {
        setManualSearchTerm('')
        setManualSearchResult(null)
        setOpenManualSearchModalType(null)
    }

    /**
     * 1. invalidTicketNumber
     * 2. wrongEventTicket
     * 3. enoughPermission
     * 4. tooEarly
     * 5. event/slotEnded
     * 6. alreadyScannedTicket
     * 7. validTicket
     */

    /**
     * @function onBackFromRecentScans
     * @description To reset recent scans state and go back to scanner tab
     * 1. Hide recent scans view
     * 2. Clear recent scans data and filtered list
     */
    function onBackFromRecentScans() {
        setShowRecentScans(false)
        setRecentScans([]);
        setFilteredRecentScans([]);
    }


    /**
     * @function parseQRCodeData
     * @description Safely parse QR code data string to JSON
     * 1. Check if string starts with '{' indicating JSON format
     * 2. Use JSON.parse to convert string to object
     * 3. Return null if not valid JSON format
     * 
     * @param {string} qrString - The QR code data string
     * @returns {object|null}
     */
    function parseQRCodeData(qrString) {
        if (qrString.startsWith('{')) {
            return JSON.parse(qrString);
        }

        return null;
    }


    /**
     * @function closeCamera
     * @description Gracefully stop camera and reset related states
     * 1. Stops the animation frame loop.
     * 2. Stops media stream
     * 3. Stops video feed
     * 4. Set camera off
     * 5. Reset qr code result
     * 6. Reset Scan result
     * 7. Close open scan result modal
     */
    function closeCamera() {

        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
            animationRef.current = null;
        }

        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }

        setIsCameraOn(false);
        setQrResult(null);
        setScanResult(null);
        setOpenScanResultModal(false);
    };


    /**
     * @function openModalAndSetVariant
     * @description Open the scan result modal with specified variant
     * 1. Set the scan result type
     * 2. Open the scan result modal
     * 
     * @param {string} modalType - The type of scan result to display. validTicket || wrongQrCode etc
     */
    function openModalAndSetVariant(modalType) {
        setScanResult(modalType);
        setOpenScanResultModal(true);
    }


    /**
     * @function closeModal
     * @description Close the scan result modal and reset related states
     * 1. Clear the scan result type
     * 2. Close the scan result modal
     * 3. Reset processing flag to allow new scans
     * 4. Optionally clear last detected QR to allow re-scan of same code
     * 5. Restart scanning if camera is still active
     */
    function closeModal() {
        setScanResult(null);
        setOpenScanResultModal(false);

        processingRef.current = false;

        lastDetectedRef.current = { value: null, ts: 0 };

        if (isCameraOn && videoRef.current && streamRef.current) {
            if (!animationRef.current) {
                startQRDetection();
            }
        }
    }


    /**
     * @function scanResultData
     * @description Memoized data derived from the latest QR scan result
     * 1. Return null if no qr code is there
     * 2. Parses the QR code data JSON string
     * 3. Finds matching ticket info from event data
     * 4. Validates event ID matches current event
     * 5. Returns structured object with event name, ticket name, and ticket ID
     * 6. Returns null if no valid QR result exists
     * 
     * @returns {object|null}
     */
    const scanResultData = useMemo(() => {

        if (!qrResult) return null;

        let parsedQRData = null;

        try {
            parsedQRData = JSON.parse(qrResult.data);
        } catch (error) {
            console.error("Error parsing QR data:", error);
        }

        const ticketInfo = parsedQRData?.ticket_type_id
            ? data?.ticket_ids?.find(ticket => ticket.id === parsedQRData.ticket_type_id)
            : null;

        const eventName = data?.event?.event_id === parsedQRData?.event_id
            ? data?.event?.title
            : "N/A";

        return {
            eventName: eventName,
            ticketName: ticketInfo ? ticketInfo.title : "N/A",
            ticketId: parsedQRData?.ticket_id || "N/A",
        }

    }, [qrResult, data])


    /**
     * @function scrollToMultipleDays
     * @description to scrolldown the page till ref is passed/defined
     */
    function scrollToMultipleDays() {
        if (multipleDaysRef.current) {
            multipleDaysRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };


    /**
     * @function onBackToViewDetails
     * @description To go back from view details tab to scanner tab
     * 1. Hide the view details tab
     * 2. Clear the view details data
     */
    function onBackToViewDetails() {
        setViewDetailsTab(false);
        // setViewDetailsData(null);
    }


    /**
     * @function onScanningMenuClick
     * @description To open the scanning menu
     * 1. Set the anchor element for the menu position
     */
    function onScanningMenuClick(event) {
        setAnchorEl(event.currentTarget);
    }


    /**
     * @function onScanningMenuClose
     * @description To close the scanning menu
     * 1. Clear the anchor element to close the menu
     */
    function onScanningMenuClose() {
        setAnchorEl(null);
    }


    /**
     * Watch the sessionId in localStorage to know that user has logged in or not.
     */
    const sessionId = localStorage.getItem('sessionId');
    const isCheckInStaffLoggedIn = useMemo(() => {
        return sessionId ? true : false;
    }, [sessionId]);


    /**
     * @function onCheckInStaffLogout
     * @description To logout the check-in staff
     * 1. Remove sessionId from localStorage
     * 2. Reload the page to reset state
     * 3. Show the toast
     */
    function onCheckInStaffLogout() {
        localStorage.removeItem('sessionId');
        window.location.reload();
        // successToastTopRight("Logged out successfully");
    }

    // console.log("isCheckInStaffLoggedIn -> ", isCheckInStaffLoggedIn)

    const eventStatus = useMemo(() => {
        const isEventDrafted = data?.event?.status === EVENT_STATUS.drafted
        const isEventCancelled = data?.event?.status === EVENT_STATUS.cancelled
        const isEventLive = data?.event?.status === EVENT_STATUS.live
        const isEventScheduled = data?.event?.status === EVENT_STATUS.scheduled

        return {
            isEventDrafted,
            isEventCancelled,
            isEventLive,
            isEventScheduled
        }
    }, [data?.event])

    return {
        eventStatus,
        data,
        error,
        loading,
        activeTab,
        countdown,
        isCountdownActive,
        setActiveTab,
        formatEventDates,
        formatEventTiming,
        startQRScanner,
        videoRef,
        closeCamera,
        isCameraOn,
        isCameraInitializing,
        qrResult,
        openModalAndSetVariant,
        closeModal,
        scanResult,
        openScanResultModal,
        scanResultData,
        checkInGuestByTicketId,
        viewDetailsTab,
        rejectGuestByTicketId,
        onViewTicketDetails,
        viewDetailsData,
        detailsLoading,
        onBackToViewDetails,
        processLoaderType,
        open,
        anchorEl,
        onScanningMenuClose,
        onScanningMenuClick,
        getAttendeesRecentHistory,
        recentScans: filteredRecentScans,
        onBackFromRecentScans,
        showRecentScans,
        onRecentScanSearch,
        isCheckInStaffLoggedIn,
        onCheckInStaffLogout,
        onManualTicketSearch,
        manualSearchTerm,
        manualSearchLoading,
        manualSearchResult,
        openManualSearchModalType,
        onManualResultDismiss,
        onChangeManualSearch,
        onManualSearchCheckIn,
        onManualSearchReject,
        onViewManualSearchTicketDetails
    }

}


/**
 * Converts a duration in seconds into a human-readable countdown string.
 * Only shows total days, hours, and minutes.
 * Limits output to top 2 units for readability.
 *
 * @param {number} seconds - Total duration in seconds
 * @returns {string} Human-readable countdown, e.g., "52 days 23 hours"
 */
function formatCountdown(seconds) {
    // Step 1: Convert total seconds into total minutes
    // Example: 3750 seconds → 62 minutes
    let totalMinutes = Math.floor(seconds / 60);

    // Step 2: Convert total minutes into total hours
    // Example: 62 minutes → 1 hour
    let totalHours = Math.floor(totalMinutes / 60);

    // Step 3: Calculate total full days
    // Example: 52 hours → 2 days
    let days = Math.floor(totalHours / 24);

    // Step 4: Calculate remaining hours after counting full days
    // Example: 52 hours → 2 days + 4 hours
    let hours = totalHours % 24;

    // Step 5: Calculate remaining minutes after counting full hours
    // Example: 125 minutes → 2 hours + 5 minutes
    let minutes = totalMinutes % 60;

    // Step 6: Build an array of string parts for each non-zero unit
    // Only add units if their value is greater than 0
    const parts = [];
    if (days > 0) parts.push(`${days} day${days > 1 ? "s" : ""}`);       // Example: 2 → "2 days"
    if (hours > 0) parts.push(`${hours} hour${hours > 1 ? "s" : ""}`);    // Example: 4 → "4 hours"
    if (minutes > 0) parts.push(`${minutes} minute${minutes > 1 ? "s" : ""}`); // Example: 5 → "5 minutes"

    // Step 7: Limit output to top 2 units for readability
    // Example: ["52 days", "23 hours", "9 minutes"] → ["52 days", "23 hours"]
    return parts.slice(0, 2).join(" ");
};

/**
 * Expected Outputs:
 * 1. If duration = 52 days 23 hours 9 minutes → "52 days 23 hours"
 * 2. If duration = 3 hours 45 minutes → "3 hours 45 minutes"
 * 3. If duration = 5 minutes → "5 minutes"
 * 4. If duration = 0 seconds → ""
 */

/**
 * event_id: 485,
 * ticket_type_id: 984,
 * ticket_id: 'TK123456'
 */

/**
 * Following are the ticket scan result possibilities
 * 1. invalidQrCodeTicket (Frontend Check)
 * 2. wrongEventTicket (Frontend Check)
 * 3. enoughPermissionsTicket (Frontend Check)
 * 4. tooEarlyTicket (Frontend Check)
 * 5. eventExpiredTicket (Frontend Check)
 * 6. validTicket (Backend Check)
 * 7. alreadyCheckedInTicket (Backend Check)
 */